'use client';

import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useState, useEffect } from 'react';
import { userErrorMessage } from '@/lib/user-error-message';
import { AI_REPORT_REASON } from '@/lib/ai-disclosure';

type ReportDialogProps = {
  productId: string;
  productName: string;
  sellerId: string;
  paymentIntentId?: string | null;
  type: 'Report' | 'Refund';
  isOpen?: boolean;
  onSubmitted?: () => void;
  onClose?: () => void;
  /** When set, the dialog submits a guest refund using the unauthenticated
   *  `submitGuestRefund` callable instead of the authed `submitDispute`. Only
   *  honoured for type === 'Refund'. */
  guest?: { orderId: string; email: string } | null;
  /** The buyer holds a live subscription to this product, so the request is for
   *  ONE billing cycle and approval also ends the subscription. Refund only. */
  subscription?: boolean;
};

const reportReasons = [
  'Product not as described',
  "Doesn't work as advertised",
  'Includes malicious content',
  'Infringes on my copyright',
  AI_REPORT_REASON,
  'Other',
];

const refundReasons = [
  'Accidental purchase',
  'Product not as described',
  "Doesn't work as advertised",
  'Purchased wrong product',
  'Other',
];

export default function ReportDialog({
  productId,
  productName,
  sellerId,
  paymentIntentId,
  type,
  isOpen,
  onSubmitted,
  onClose,
  guest,
  subscription,
}: ReportDialogProps) {
  const { toast } = useToast();
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset form whenever dialog is closed
  useEffect(() => {
    if (!isOpen) {
      setReason('');
      setDescription('');
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    if (!reason) {
      toast({ title: 'Please select a reason', variant: 'destructive' });
      return;
    }
    if (!description.trim()) {
      toast({ title: 'Please provide a description', variant: 'destructive' });
      return;
    }
    if (type === 'Refund' && !paymentIntentId && !guest) {
      toast({ title: 'Cannot submit refund', description: 'No purchase record found.', variant: 'destructive' });
      return;
    }
    setIsSubmitting(true);
    try {
      const fns = getFunctions();
      let result: { data?: unknown };
      if (type === 'Refund' && guest) {
        const submitGuestRefund = httpsCallable(fns, 'submitGuestRefund');
        result = await submitGuestRefund({
          orderId: guest.orderId,
          email: guest.email,
          productId,
          reason,
          description,
        });
      } else {
        const submitDispute = httpsCallable(fns, 'submitDispute');
        result = await submitDispute({
          sellerUid: sellerId,
          productId,
          productName,
          paymentIntentId: paymentIntentId || null,
          type,
          reason,
          description,
        });
      }
      // A product whose seller has closed their account is refunded by us
      // (functions/src/lib/refund-request-route.ts), so say who decides it.
      const reviewedByUs = (result.data as { reviewedBy?: unknown } | undefined)?.reviewedBy === 'supa.place';
      toast({
        title: type === 'Report' ? 'Report submitted' : 'Refund request submitted',
        description:
          type === 'Report'
            ? 'A member of our team reviews every report and will take action where needed.'
            : reviewedByUs
              ? "The seller has closed their account, so we'll review it ourselves and email you our decision."
              : "We've sent your request to the seller. If they haven't decided within 14 days, it comes to us.",
      });
      if (onSubmitted) onSubmitted();
      if (onClose) onClose();
    } catch (err: any) {
      toast({
        title: type === 'Report' ? 'Could not submit the report' : 'Could not submit the refund request',
        description: userErrorMessage(err, 'What you wrote is still here. Try again in a moment.'),
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DialogContent
      onEscapeKeyDown={(e) => e.preventDefault()}
      onPointerDownOutside={(e) => e.preventDefault()}
    >
      <DialogHeader>
        <DialogTitle>{type === 'Refund' ? 'Request a refund' : 'Report this product'}</DialogTitle>
        <DialogDescription>
          Please provide details about your {type.toLowerCase()}.
        </DialogDescription>
      </DialogHeader>
      {productName && (
        <div className="rounded-md border bg-muted/30 px-3 py-2">
          <p className="truncate text-sm">{productName}</p>
        </div>
      )}
      {/* A subscription is billed one cycle at a time and each cycle is its own
          charge, so a refund returns the CURRENT one. Said before the buyer
          writes their request, because approval also ends their access. */}
      {type === 'Refund' && subscription && (
        <p className="rounded-md bg-secondary px-3 py-2 text-xs text-muted-foreground">
          This asks for your most recent subscription charge back. If the seller approves it,
          your subscription is cancelled and your access ends straight away. To simply stop future
          charges and keep access until the end of the cycle you have paid for, cancel the
          subscription instead.
        </p>
      )}
      <div className="py-4 space-y-4">
        <div className="space-y-2">
          <Label htmlFor="reason">Reason</Label>
          <Select value={reason} onValueChange={setReason}>
            <SelectTrigger id="reason">
              <SelectValue placeholder="Select a reason" />
            </SelectTrigger>
            <SelectContent>
              {(type === 'Report' ? reportReasons : refundReasons).map(r => (
                <SelectItem key={r} value={r}>{r}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            placeholder="Provide a detailed description of the issue..."
            rows={5}
            value={description}
            maxLength={500}
            onChange={e => setDescription(e.target.value.slice(0, 500))}
          />
          <p className={`text-xs tabular-nums text-right ${description.length > 500 ? 'text-destructive' : 'text-muted-foreground'}`}>
            {description.length}/500
          </p>
        </div>
      </div>
      <DialogFooter>
        <DialogClose asChild>
          <Button variant="outline" disabled={isSubmitting}>Cancel</Button>
        </DialogClose>
        <Button
          onClick={handleSubmit}
          disabled={!reason || !description.trim()}
          loading={isSubmitting}
        >
          {isSubmitting
            ? 'Submitting…'
            : type === 'Refund' ? 'Submit refund request' : 'Submit report'}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
